import { useToast } from '@ais-ui/ui-core';
import React, { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useEditScheduleMessage,
  useFetchPSSWithType,
  useScheduleMessage,
  useShapeFetch,
} from '@ais-ui/api';
import { mutateCallbacks } from '../../utils/mutateCallbacks';
import { useLocalization } from '@ais-ui/locale-management';
import {
  convertBackendToMessageScheduleValues,
  convertScheduleMessageValuesToBackend,
  scheduleMessageFormSchema,
  ScheduleMessageFormValues,
} from './scheduleMessageForm';
import { ScheduleMessageDialog } from './ScheduleMessageDialog';
import { Schedule } from '@ais-ui/api-spec/config';
import { useGetShareGroups } from '@ais-ui/websockets';

interface ScheduleMessageProps {
  setShowDialog: (showDialog: boolean) => void;
  messageSchedule: Schedule;
}

export const EditMessageSchedule = ({
  setShowDialog,
  messageSchedule,
}: ScheduleMessageProps) => {
  const { messages } = useLocalization();
  const { mutate } = useEditScheduleMessage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const shapeFetch = useShapeFetch();

  const { data: groups, isLoading: isLoadingGroups } = useGetShareGroups();
  const { data: pss, isLoading: isLoadingPss } = useFetchPSSWithType('aton');
  const [hasLoadedValues, setHasLoadedValues] = useState<boolean>(false);

  const { toast } = useToast();

  const form = useForm<ScheduleMessageFormValues>({
    resolver: zodResolver(scheduleMessageFormSchema),
  });

  useEffect(() => {
    // Check if both data sources have been loaded and set the dataLoaded state
    if (!isLoadingGroups && !isLoadingPss) {
      const defaultValues = convertBackendToMessageScheduleValues(
        messageSchedule,
        groups,
        pss?.records
      );
      form.reset(defaultValues);
      setHasLoadedValues(true);
    }
  }, [
    messageSchedule,
    groups,
    isLoadingGroups,
    isLoadingPss,
    pss?.records,
    form,
  ]);

  const onScheduleMessage = async (data: ScheduleMessageFormValues) => {
    let shape;
    if (data.shapeId) {
      shape = await shapeFetch(data.shapeId);
    }

    const backendValues = convertScheduleMessageValuesToBackend(data, shape);

    setIsSubmitting(true);

    const callBacks = mutateCallbacks(
      toast,
      messages,
      'edit',
      'message schedule',
      setIsSubmitting,
      setShowDialog
    );

    mutate({ messageSchedulePutRequest: backendValues }, callBacks);
  };

  if (!hasLoadedValues) {
    return null;
  }

  return (
    <ScheduleMessageDialog
      form={form}
      onSubmit={onScheduleMessage}
      mutateType="edit"
      isSubmitting={isSubmitting}
      setShowOuterDialog={setShowDialog}
    ></ScheduleMessageDialog>
  );
};
